import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { withNamespaces } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Card, CardHeader, CardBody, Col, Form, FormGroup, Input, Label, NavItem, NavLink, Row, Spinner, Table } from "reactstrap";
import EudrDeclarationService from "src/modules/3rd-party-management/apis/thirdparty/EudrDeclarationService";
import { DDSTypes } from "src/modules/3rd-party-management/constants";
import InProcess from "./components/diligence-types/InProcess";
import NotObliged from "./components/diligence-types/NotObliged";
import GeoLocation from "./components/diligence-types/GeoLocation";
import ReferenceNumber from "./components/diligence-types/ReferenceNumber";
import UploadFile from "./components/diligence-types/UploadFile";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import DateUtils from "src/services/utils/DateUtils";
import ButtonsGroup from "./components/ButtonsGroup";
import GeolocationOverview from "./components/GeolocationOverview";

const CreateBatch = ({ 
    t
}) => {
    const [tabs, setTabs] = useState([
        {
            name: 'select-products',
            title: 'Selected Products',
            completed: false,
            isCurrent: true
        },
        {
            name: 'select-dds-type',
            title: 'Select DDS Declaration Type',
            completed: false,
            isCurrent: false
        },
        {
            name: 'required-information',
            title: 'Provide Required Information',
            completed: false,
            isCurrent: false
        },
        {
            name: 'save',
            title: 'Review & Save',
            completed: false,
            isCurrent: false
        }
    ]);
    const [ddsType, setDdsType] = useState("");
    const [ddsData, setDdsData] = useState({});

    const location = useLocation();
    const history = useHistory();

    const dateUtils = new DateUtils();

    const params = new URLSearchParams(location.search);

    const productsIds = params.get('products', '');

    const {
        isFetching: productsAreFetching,
        data: products
    } = useQuery({
        queryKey: [
            '3rd-party-management-search-supplier-eudr-products',
            productsIds
        ],
        queryFn: async () => {
            const service = EudrDeclarationService.getInstance();

            return await service.searchByIds(null, {
                ids: productsIds
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        }
    });

    const {
        mutate: declareMutation,
        isLoading: declarationIsLoading
    } = useMutation({
        mutationFn: async (payload) => {
            const service = EudrDeclarationService.getInstance();
            return await service.declare(payload);
        },
        onSuccess: () => {
            toast(t("Your due diligence declaration has been saved successfully."), {
                type: "success"
            });
            history.push('/third-party/3rd-party/eudr-due-diligence');
        },
        onError: () => {
            toast(t("Failed to save your due diligence declaration. Please try again."), {
                type: "error"
            });
        }
    });

    const setActiveTab = (tab) => {
        setTabs((tabs) => {
            const currentActiveTabIndex = tabs.findIndex((t) => t.isCurrent);
            const nextActiveTabIndex = tabs.findIndex((t) => t.name === tab);

            if (currentActiveTabIndex > -1) {
                tabs[currentActiveTabIndex].completed = true;
                tabs[currentActiveTabIndex].isCurrent = false;
            }

            if (nextActiveTabIndex > -1) {
                tabs[nextActiveTabIndex].isCurrent = true;
            }

            return [
                ...tabs
            ];
        });
    }

    const saveBatch = () => {
        declareMutation({
            productIds: products.map((p) => p.id),
            ddsDeclarationType: ddsType,
            ...ddsData
        });
    }

    const handleDDSTypeFormSubmit = (values) => {
        setDdsData(values);
        setActiveTab('save');
    }

    const activeTab = tabs.find((t) => t.isCurrent);

    const renderStepsWizard = () => {
        const currentActiveTabIndex = tabs.findIndex((t) => t.isCurrent);

        return (
            <Row>
                <Col sm='12'>
                    <div className="m-2 bg-white wizard-steps-container mb-5">
                        <div className="twitter-bs-wizard">
                            <ul className="twitter-bs-wizard-nav nav-justified nav nav-pills p-3">
                                {
                                    tabs.map((tab, index) => {
                                        return (
                                            <NavItem key={index} className={tab.completed ? 'completed' : ''}>
                                                <NavLink className={(index <= currentActiveTabIndex ? 'active ' : '') + (tab.isCurrent ? 'current' : '')}>
                                                    <span className="step-number">
                                                        {
                                                            tab.completed ? (
                                                                <i className='fa fa-check'></i>
                                                            ) : (index + 1)
                                                        }
                                                    </span>

                                                    <span className="step-title">
                                                        {t(tab.title)}
                                                    </span>
                                                </NavLink>
                                            </NavItem>
                                        );
                                    })
                                }
                            </ul>
                        </div>
                    </div>
                </Col>
            </Row>
        )
    }

    return (
        <div>
            {renderStepsWizard()}

            <Row>
                <Col sm='12'>
                    {activeTab.name === 'select-products' && (
                        <Card>
                            <CardHeader className="bg-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <span className="badge bg-primary rounded-pill me-2">
                                        {t('Step 1')}
                                    </span>
                                    {t('Choose products for this declaration')}
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t('NACE Code')}</th>
                                            <th>{t('Product Name')}</th>
                                            <th>{t('Lot Number')}</th>
                                            <th>{t("Country of Origin")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {productsAreFetching ? (
                                            <tr>
                                                <td colSpan='5' className="text-center">
                                                    <Spinner size='sm' color="primary" /><br />
                                                    {t('Loading')}...
                                                </td>
                                            </tr>
                                        ) : (
                                            <>
                                                {(products || []).map((product, index) => (
                                                    <tr>
                                                        <td 
                                                            style={{width: '10%'}}
                                                        >
                                                            {index + 1}
                                                        </td>
                                                        <td 
                                                            style={{width: '30%'}}
                                                        >
                                                            {`${product.name.class}-${t(product.name.name)}`}
                                                        </td>
                                                        <td 
                                                            style={{width: '20%'}}
                                                        >
                                                            {product.product_name}
                                                        </td>
                                                        <td 
                                                            style={{width: '20%'}}
                                                        >
                                                            {product.lot_number}
                                                        </td>
                                                        <td 
                                                            style={{width: '20%'}}
                                                        >
                                                            {t(product.origin_country?.name)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </>
                                        )}
                                    </tbody>
                                </Table>

                                <ButtonsGroup 
                                    onNextClicked={() => setActiveTab('select-dds-type')}
                                    isLoading={productsAreFetching}
                                />
                            </CardBody>
                        </Card>
                    )}

                    {activeTab.name === 'select-dds-type' && (
                        <Card>
                            <CardHeader className="bg-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <span className="badge bg-primary rounded-pill me-2">
                                        {t('Step 2')}
                                    </span>
                                    {t('How will you provide your due diligence statement?')}
                                </h5> 
                            </CardHeader>
                            <CardBody>
                                <Form>
                                    {Object.keys(DDSTypes).map(
                                        (type) => (
                                            <FormGroup check key={type}>
                                                <Label check>
                                                    <Input
                                                        type="radio"
                                                        name="ddsType"
                                                        value={type}
                                                        checked={ddsType === type}
                                                        onChange={(e) => setDdsType(e.target.value)}
                                                    />
                                                    {t(DDSTypes[type].title)}
                                                </Label>
                                            </FormGroup>
                                        )
                                    )}
                                </Form>

                                <ButtonsGroup 
                                    onBackClicked={() => setActiveTab('select-products')}
                                    onNextClicked={() => setActiveTab('required-information')}
                                    nextIsDisabled={!ddsType}
                                />
                            </CardBody>
                        </Card>
                    )}

                    {activeTab.name === 'required-information' && (
                        <Card>
                            <CardHeader className="bg-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <span className="badge bg-primary rounded-pill me-2">
                                        {t('Step 3')}
                                    </span>
                                    {t('Enter the required information')}
                                </h5> 
                            </CardHeader>
                            <CardBody>
                                {ddsType === "provide_reference_numbers" && (
                                    <ReferenceNumber 
                                        onValidSubmit={handleDDSTypeFormSubmit}
                                        onBackClicked={() => setActiveTab('select-dds-type')}
                                        prevData={ddsData}
                                    />
                                )}

                                {ddsType === "upload_document" && (
                                    <UploadFile 
                                        onValidSubmit={handleDDSTypeFormSubmit}
                                        onBackClicked={() => setActiveTab('select-dds-type')}
                                        prevData={ddsData}
                                    />
                                )}

                                {ddsType === "not_obliged" && (
                                    <NotObliged 
                                        onValidSubmit={handleDDSTypeFormSubmit}
                                        onBackClicked={() => setActiveTab('select-dds-type')}
                                        prevData={ddsData}
                                    />
                                )}

                                {ddsType === "in_process" && (
                                    <InProcess 
                                        onValidSubmit={handleDDSTypeFormSubmit}
                                        onBackClicked={() => setActiveTab('select-dds-type')}
                                        prevData={ddsData}
                                    />
                                )}

                                {ddsType === "geo_location" && (
                                    <GeoLocation 
                                        onValidSubmit={handleDDSTypeFormSubmit}
                                        onBackClicked={() => setActiveTab('select-dds-type')}
                                        prevData={ddsData}
                                        products={products}
                                    />
                                )}
                            </CardBody>
                        </Card>
                    )}

                    {activeTab.name === 'save' && (
                        <Card>
                            <CardHeader className="bg-white">
                                <h5 className="mb-0 d-flex align-items-center">
                                    <span className="badge bg-primary rounded-pill me-2">
                                        {t('Step 4')}
                                    </span>
                                    {t('Review and confirm your declaration')}
                                </h5> 
                            </CardHeader>
                            <CardBody>
                                <h5>{t('Products')}:</h5>
                                <ul>
                                    {products.map((p) => (
                                        <li key={p.id}>
                                            {`${p.name.class}-${t(p.name.name)}`} ({t('Lot')} {p.lot_number}, {t('Origin')}: {p.origin_country?.name})
                                        </li>
                                    ))}
                                </ul>

                                <p>
                                    <strong>{t('Declaration Type')}:</strong> {t(DDSTypes[ddsType].title)}
                                </p>

                                {ddsType === 'provide_reference_numbers' && (
                                    <p>
                                        <span className="d-block mb-3">
                                            <strong>
                                                {t('DDS Reference Number')}:
                                            </strong>{' '}
                                            {ddsData.ddsReferenceNumber}
                                        </span>
                                        <span className="d-block">
                                            <strong>
                                                {t('DDS Verification Number')}:
                                            </strong>{' '}
                                            {ddsData.ddsVerificationNumber}
                                        </span>
                                    </p>
                                )}

                                {ddsType === 'not_obliged' && (
                                    <p>
                                        <strong>{t('Justification')}:</strong>{' '}
                                        {ddsData.ddsNotes}
                                    </p>
                                )}

                                {ddsType === 'in_process' && (
                                    <p>
                                        <strong>{t('Expected Completion Date')}:</strong>{' '}
                                        {dateUtils.convertDateToDate(ddsData.expectedDdsDate, INTERNATIONAL_DATE_FORMAT)}
                                    </p>
                                )}

                                {ddsType === 'geo_location' && (
                                    <GeolocationOverview
                                        locations={ddsData}
                                    />
                                )}

                                <ButtonsGroup
                                    onBackClicked={() => setActiveTab('required-information')}
                                    onSaveClicked={saveBatch}
                                    isLoading={declarationIsLoading}
                                />
                            </CardBody>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    )
};

export default withNamespaces()(
    CreateBatch
);